import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { AppContext } from "../App";
import { FiArrowUpRight } from "react-icons/fi";
import { getInlineLoader } from "../Helpers";
import Footer from "./Footer";
import OnePortfolio from "./OnePortfolio";

export default function Portfolios() {
  const sectionRef = useRef(null);
  const appContext = useContext(AppContext);
  const isPortfolioPage = appContext?.navItem === "portfolio";

  const [ready, setReady] = useState(false);
  const [filter, setFilter] = useState("All");

  // Initialize data fetching following the exact flow of Products
  async function init() {
    setReady(false);
    if (typeof appContext.getAllPortfolios === "function") {
      await appContext.getAllPortfolios();
    }
    setReady(true);
  }

  useEffect(() => {
    init();
  }, []);

  // Sort portfolios by dateCreated descending
  const sortedPortfolios = useMemo(() => {
    const allItems = Array.isArray(appContext.allPortfolios)
      ? [...appContext.allPortfolios]
      : Array.isArray(appContext.portfolios)
      ? [...appContext.portfolios]
      : [];
    return allItems.sort(
      (a, b) => Number(b?.dateCreated || 0) - Number(a?.dateCreated || 0)
    );
  }, [appContext.allPortfolios, appContext.portfolios]);

  // Handle preview mode slicing vs filter logic
  const displayProjects = useMemo(() => {
    if (!isPortfolioPage) {
      return sortedPortfolios.slice(0, 2);
    }
    if (filter === "All") {
      return sortedPortfolios;
    }
    return sortedPortfolios.filter(
      (p) =>
        (p.type && p.type.toLowerCase() === filter.toLowerCase()) ||
        (p.category && p.category.toLowerCase() === filter.toLowerCase())
    );
  }, [sortedPortfolios, isPortfolioPage, filter]);

  // Intersection Observer setup for animation triggers
  useEffect(() => {
    if (!ready || !sectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("portfolio-active");
          }
        });
      },
      { threshold: 0.1 }
    );

    const revealElements = sectionRef.current.querySelectorAll(".portfolio-reveal");
    revealElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [ready, displayProjects]);

  if (!ready) {
    return (
      <div className="container py-5 text-center">
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <>
      <section className="wer-portfolio-section" ref={sectionRef}>
        <style>{`
          .wer-portfolio-section { 
            background: #ffffff; 
            padding: clamp(100px, 10vh, 120px) 0; 
            font-family: 'Inter', sans-serif; 
          }
          
          .w-header-title {
            font-size: clamp(2.5rem, 8vw, 4.5rem);
            font-weight: 900;
            line-height: 0.9;
            letter-spacing: -0.05em;
            color: var(--secondaryColor);
            margin-bottom: 1.5rem;
          }

          .portfolio-filter-bar {
            display: flex;
            gap: 6px;
            background: #f1f5f9;
            padding: 5px;
            border-radius: 16px;
            width: fit-content;
            border: 1px solid #e2e8f0;
          }

          .filter-btn {
            padding: 10px 22px;
            border-radius: 12px;
            border: none;
            background: transparent;
            color: #64748b;
            font-weight: 600;
            font-size: 0.85rem;
            cursor: pointer;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .filter-btn.active {
            background: #ffffff;
            color: var(--secondaryColor);
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          }

          /* ── Card Styling ── */
          .portfolio-card {
            position: relative;
            border-radius: 32px;
            overflow: hidden;
            background: #fff;
            border: 1px solid #f1f5f9;
            transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
            height: 100%;
          }

          .portfolio-card:hover {
            transform: translateY(-10px);
            border-color: var(--secondaryColor);
            box-shadow: 0 30px 60px -20px rgba(0,0,0,0.15);
          }

          .img-wrapper {
            aspect-ratio: 16/11;
            overflow: hidden;
            background: #f8fafc;
          }

          .img-wrapper img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.8s cubic-bezier(0.23, 1, 0.32, 1);
          }

          .portfolio-card:hover .img-wrapper img {
            transform: scale(1.08);
          }

          .portfolio-info {
            padding: 2rem;
            position: relative;
          }

          .project-type-tag {
            font-family: 'JetBrains Mono', monospace;
            font-size: 0.7rem;
            color: var(--secondaryColor);
            text-transform: uppercase;
            letter-spacing: 2px;
            font-weight: 700;
            display: block;
            margin-bottom: 0.5rem;
          }

          .portfolio-reveal { 
            opacity: 0; 
            transform: translateY(40px); 
            transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1); 
          }

          .portfolio-active { opacity: 1; transform: translateY(0); }

          .tag-pill {
            font-size: 0.7rem;
            background: #f8fafc;
            padding: 6px 14px;
            border-radius: 100px;
            color: #475569;
            font-weight: 600;
            border: 1px solid #e2e8f0;
          }

          .btn-view-all {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 18px 40px;
            border-radius: 100px;
            background: var(--primaryColor);
            color: #fff;
            font-weight: 700;
            transition: 0.4s;
            border: none;
            box-shadow: 0 10px 20px rgba(15, 23, 42, 0.2);
            cursor: pointer;
          }

          .btn-view-all:hover {
            background: var(--secondaryColor);
            transform: scale(1.05);
            color: #ffffff;
          }

          @media (max-width: 991px) {
            .portfolio-filter-bar { width: 100%; overflow-x: auto; margin-top: 2rem; }
            .w-header-title { line-height: 1.1; }
          }
        `}</style>

        <div className="container">
          <div className="row align-items-center mb-5">
            <div className="col-lg-6">
              <h2 className="w-header-title">Our Portfolio</h2>
              <p className="text-muted lead fw-medium">
                Crafting high-performance digital experiences.
              </p>
            </div>

            <div className="col-lg-6 d-flex justify-content-lg-end">
              {isPortfolioPage && (
                <div className="portfolio-filter-bar d-none">
                  {["All", "Apps", "Websites", "Systems"].map((cat) => (
                    <button
                      key={cat}
                      className={`filter-btn ${
                        filter === cat ? "active" : ""
                      }`}
                      onClick={() => setFilter(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="row g-5">
            {displayProjects.length > 0 ? (
              displayProjects.map((project) => (
                <OnePortfolio key={project.id || project.uuid} data={project} />
              ))
            ) : (
              <div className="col-12 text-center text-muted py-5">
                <h6>No portfolio items found</h6>
              </div>
            )}
          </div>

          {!isPortfolioPage && (
            <div className="view-more-wrapper mt-5">
              <button
                className="btn-view-all mx-auto"
                onClick={() => appContext.navTo({ item: "portfolio" })}
              >
                Browse Our Portfolio <FiArrowUpRight size={20} />
              </button>
            </div>
          )}
        </div>
      </section>
      {appContext.navItem === "portfolio" ? <Footer /> : ""}
    </>
  );
}